import React from 'react';
import { useCardContext } from '../cards/CardContext';
import { css } from '@emotion/react';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { useThemeContext } from '$/components/ui/ThemeContext';
import { FilledMarkdown } from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { FormOptionsContext } from '$/components/ui/Form';
import PageBuilder from '$/components/ui/Input/PageBuilder';
import { MediaHint } from '$/GraphQL/schema/types';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import { colorWithAlpha, getReadableColorOnWhite } from '$/utils/colors';
import { useInteractionIconColor } from '$/hooks/useInteractionIconColor';
import { langDirClassname } from '$/shared/frontend/utils/i18n';

const TestimonialCard: React.FC = withWebinarPlayerContext(['ewebinar'])(({ context }) => {
  const { interaction } = useCardContext();
  const { ewebinar } = context;
  const etheme = useThemeContext();
  const Icon = interaction.icon();

  const { css: iconColorCss } = useInteractionIconColor(interaction, ewebinar);

  const dir = langDirClassname(ewebinar.viewerRoomSettings?.language);
  const avatarRef = interaction.details?.testimonial?.avatarMediaRef;
  const companyLink = interaction.details?.testimonial?.companyLink;
  const actionLink = interaction.details?.link;
  const { onSubmit } = React.useContext(FormOptionsContext);
  return (
    <div
      className='p-1e rounded-2xl text-md text-color-cardText'
      css={css`
        background: linear-gradient(
          to left top,
          ${colorWithAlpha(ewebinar.primaryColor, 0.25)},
          #ffffff 66%
        );
        border: 1px solid ${etheme.player.card.border};
        font-family: '${ewebinar.font ?? 'unset'}' !important;
      `}
    >
      <div className='flex items-start justify-between '>
        <div className='flex flex-col'>
          <FilledMarkdown
            css={css`
              font-size: 1.3rem;
            `}
            md={interaction.details?.testimonial?.quote ?? ''}
          />
          {actionLink && (
            <div
              className={`mt-1/4e transition-transform duration-200 cursor-pointer`}
              css={css`
                font-size: 1.1rem;
                transform-origin: center left;
                &:hover:not([disabled]) {
                  transform: scale(1.04);
                }
              `}
              onClick={(): void => {
                onSubmit();
              }}
            >
              <FilledMarkdown
                md={interaction.details?.testimonial?.action ?? ''}
                css={css`
                  color: ${getReadableColorOnWhite(ewebinar.actionColor)};
                `}
              />
            </div>
          )}
        </div>
        {Icon && (
          <Icon
            css={css`
              ${iconColorCss}
              height: 1.75rem;
              width: 1.75rem;
              margin-top: 0.25rem;
              ${dir === 'ltr'
                ? `
                margin-right: 0;
                margin-left: 0.75rem;`
                : `
                margin-left: 0;
                margin-right: 0.75rem;
            `}
            `}
          />
        )}
      </div>

      <div className='flex gap-3 mt-1e'>
        {avatarRef && (
          <PageBuilder.Avatar
            value={avatarRef}
            asset={{
              entity: 'ewebinar',
              entityId: ewebinar.id,
              name: `testimonial.imageUrl`,
              container: {
                width: 80,
                height: 80,
                hint: MediaHint.Profile,
              },
            }}
            state={{ mode: PageMode.public }}
          />
        )}

        <div className='flex flex-col justify-center'>
          <FilledMarkdown
            className='font-semibold text-xl'
            md={interaction.details?.testimonial?.name ?? ''}
          />
          <FilledMarkdown md={interaction.details?.testimonial?.title ?? ''} />
          <div
            className={companyLink ? 'transition-transform duration-200 cursor-pointer' : ''}
            css={
              companyLink
                ? css`
                    transform-origin: center left;
                    &:hover:not([disabled]) {
                      transform: scale(1.04);
                    }
                  `
                : undefined
            }
            onClick={(): void => {
              if (!companyLink) return;
              window.open(companyLink, '_blank');
            }}
          >
            <FilledMarkdown
              md={interaction.details?.testimonial?.company ?? ''}
              css={css`
                & > p,
                & > p > a {
                  color: ${getReadableColorOnWhite(ewebinar.primaryColor)};
                }
              `}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default TestimonialCard;
